import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { Settings } from "../../../settings";
import { selectBet, selectFreeRoundsActive, selectFreeRoundsBet } from "../../../State/selectors";
import OverlayReelController from "./OverlayReelController";
import { safeInit } from "../../../utilities";
import { FEATURES_CONFIG } from "../../../featuresConfig";

const OverlayReel = forwardRef(
  ({ index, symbolWidth, symbolHeight, reelSpacing, symbolRefs }, ref) => {
    const overlayReelControllerRef = useRef(null);
    const reelContainerRef = useRef(null);
    // Latest bet, readable from the mount effect's stale closure. The
    // controller can be built a frame or more after mount (see the readiness
    // retry below), and the [bet] effect below bails out while the controller
    // is still null — so without this a deferred build would freeze the
    // mount-time bet into every coin value text.
    const betRef = useRef(null);
    
    // Use free rounds bet when active, otherwise use regular bet - memoized
    const freeRoundsActive = useSelector(selectFreeRoundsActive);
    const freeRoundsBet = useSelector(selectFreeRoundsBet);
    const regularBet = useSelector(selectBet);
    const bet = useMemo(
      () =>
        FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive && freeRoundsBet
          ? freeRoundsBet
          : regularBet,
      [freeRoundsActive, freeRoundsBet, regularBet]
    );

    // Memoize container X position
    const containerX = useMemo(
      () => reelSpacing * (index - (Settings.REEL_COUNT - 1) / 2),
      [reelSpacing, index]
    );

    // ✅ Initialize controller once when component mounts
    //
    // The 3 symbol containers are rendered by OverlayReelSet now, not here, so
    // they arrive as a prop. React attaches every ref in the commit phase
    // before any passive effect runs, and the symbols are emitted ahead of the
    // reels, so they should already be populated — but the controller calls
    // overlaySymbol.addChild() during construction, and a null there would
    // silently leave a reel with no spines. Retry on rAF instead of assuming,
    // same guard PotCollectionOverlaySymbol uses.
    useEffect(() => {
      let rafId = null;

      const tryInit = () => {
        rafId = null;
        if (overlayReelControllerRef.current) return;

        // Counted explicitly rather than with .every() — the refs arrive in a
        // `new Array(SYMBOLS_VISIBLE)`, and every()/filter() SKIP holes in a
        // sparse array, so `.every(Boolean)` reports true on an array that is
        // completely empty. That would wave through a controller with
        // undefined cells and leave the reel with no spines at all.
        let attachedCount = 0;
        for (let i = 0; i < Settings.SYMBOLS_VISIBLE; i++) {
          if (symbolRefs?.[i]) attachedCount++;
        }

        const ready =
          reelContainerRef.current &&
          attachedCount === Settings.SYMBOLS_VISIBLE;

        if (!ready) {
          rafId = requestAnimationFrame(tryInit);
          return;
        }

        safeInit(() => {
          overlayReelControllerRef.current = new OverlayReelController(
            symbolRefs,
            index,
            symbolWidth,
            symbolHeight,
            reelContainerRef.current,
            betRef.current ?? bet
          );
        });
      };

      tryInit();

      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        if (overlayReelControllerRef.current) {
          overlayReelControllerRef.current.cleanup();
          overlayReelControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ Handle resize of all overlay symbols
    useEffect(() => {
      if (overlayReelControllerRef.current) {
        overlayReelControllerRef.current.resizeAnimations(
          symbolWidth,
          symbolHeight
        );
      }
    }, [symbolHeight, symbolWidth]);

    // 💰 Bet update - update coin values when bet changes
    useEffect(() => {
      // Tracked unconditionally: a controller built later reads this instead of
      // the mount effect's captured bet.
      betRef.current = bet;
      if (!overlayReelControllerRef.current) return;
      overlayReelControllerRef.current.updateBet(bet);
    }, [bet]);

    // ✅ Expose all imperative overlay reel controls
    useImperativeHandle(ref, () => ({
      getOverlayReelController: () => overlayReelControllerRef.current,

      showWinAnimation: (symbolIndex, symbolId, animationType = "win") => {
        overlayReelControllerRef.current?.showWinAnimation(
          symbolIndex,
          symbolId,
          animationType
        );
      },

      showAppearAnimation: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimation(
          symbolIndex,
          symbolId
        );
      },

      showAppearAnimationAtPosition: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimationAtPosition(
          symbolIndex,
          symbolId
        );
      },

      hideWinAnimation: (symbolIndex) => {
        overlayReelControllerRef.current?.hideWinAnimation(symbolIndex);
      },

      hideAllAnimations: () => {
        overlayReelControllerRef.current?.hideAllAnimations();
      },

      hideAllAnimationsExcept: (upcomingPositions) => {
        overlayReelControllerRef.current?.hideAllAnimationsExcept(
          upcomingPositions
        );
      },

      replaceVisibleSymbols: (targetSymbolId) => {
        overlayReelControllerRef.current?.replaceVisibleSymbols(targetSymbolId);
      },

      playExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReel();
      },

      playExpandingWildFullReelWin: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReelWin();
      },

      hideExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.hideExpandingWildFullReel();
      },

      forceStopExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.forceStopExpandingWildFullReel();
      },

      playExpandingWildDarkAnimation: () => {
        overlayReelControllerRef.current?.playExpandingWildDarkAnimation();
      },

      playExpandingWildWinAnimation: () => {
        overlayReelControllerRef.current?.playExpandingWildWinAnimation();
      },
    }));

    // This container no longer holds the reel's symbols — that nesting is
    // exactly what made per-symbol z-index impossible, since children can only
    // sort against siblings. Its sole remaining job is hosting the expanding
    // wild spine, which is full-reel height and so can't live in a symbol cell.
    // It keeps the same x, so the spine's reel-local coordinates
    // (position.set(0,0), scaleData.x) still mean what they always meant.
    return (
      <Container
        ref={reelContainerRef}
        name={`spine_overlay_ew_${index}`}
        x={containerX}
        zIndex={15}
      />
    );
  }
);

export default OverlayReel;
